import {
  formatarTelefone,
  type AtendenteDTO,
  type Configuracoes,
  type ConversaDetalheDTO,
  type OrigensOrcamento,
} from '@viniko/shared';
import { Pencil, X } from 'lucide-react';
import { AvatarContato, StatusTexto, useToast } from '../../componentes/ui';
import { useConfiguracoes } from '../../lib/consultas';
import { useCorAtendente } from '../../lib/tema';
import { CadastroTiny } from '../../componentes/CadastroTiny';
import { CalcularItem } from './CalcularItem';
import { Midias } from './Midias';
import { Observacoes } from './Observacoes';
import { Pedido } from './Pedido';

function situacaoIA(c: ConversaDetalheDTO, config: Configuracoes | undefined): string {
  if (c.status === 'bot') return c.iaAtiva ? 'Atendendo' : 'Pausada';
  if (c.status === 'atendimento') return 'Parada (atendente assumiu)';
  if (c.status === 'finalizada') return '—';
  const espera =
    config?.ia.ativa &&
    config.ia.duvidasNaFila &&
    !['IA desligada', 'Falha da IA'].includes(c.motivoTransferencia ?? '');
  return espera ? 'Tirando dúvidas' : 'Pausada';
}

const ORIGEM = { whatsapp: 'WhatsApp', instagram: 'Instagram', anuncio: 'Anúncio' } as const;

export function PainelContato({
  conversa: c,
  eu,
  aberto,
  fechar,
  editar,
  etiquetas,
}: {
  conversa: ConversaDetalheDTO;
  eu: AtendenteDTO;
  aberto: boolean;
  fechar: () => void;
  editar: () => void;
  etiquetas: () => void;
}) {
  const cor = useCorAtendente(c.atendente?.cor);
  const config = useConfiguracoes().data;
  return (
    <aside className={`painel ${aberto ? 'aberto' : ''}`} aria-label="Detalhes do contato">
      <button
        className="icone-btn sem-borda painel-fechar"
        aria-label="Fechar painel"
        onClick={fechar}
      >
        <X />
      </button>

      <div className="contato">
        <AvatarContato
          id={c.contato.id}
          nome={c.contato.nome}
          fotoUrl={c.contato.fotoUrl}
          tamanho={72}
        />
        <span className="contato-nome">
          <b>{c.contato.nome}</b>
          <button
            className="icone-btn sem-borda"
            aria-label="Editar contato"
            title="Editar contato"
            onClick={editar}
          >
            <Pencil />
          </button>
        </span>
        {c.contato.telefone && <span className="num">{formatarTelefone(c.contato.telefone)}</span>}
        {(c.contato.empresa || c.contato.cidade) && (
          <span className="contato-extra">
            {[c.contato.empresa, c.contato.cidade].filter(Boolean).join(' · ')}
          </span>
        )}
        <span className="contato-tags">
          {c.contato.recorrente && <span className="contato-tag">Cliente recorrente</span>}
          {c.contato.bloqueado && <span className="bloqueado-tag">Bloqueado</span>}
        </span>
      </div>

      <section className="sec">
        <h2>Observações</h2>
        <Observacoes conversa={c} eu={eu} />
      </section>

      <section className="sec">
        <h2>Atendimento</h2>
        <dl className="kv">
          <dt>Status</dt>
          <dd>
            <StatusTexto status={c.status} />
          </dd>
          {c.motivoTransferencia && c.status !== 'bot' && (
            <>
              <dt>Motivo</dt>
              <dd>{c.motivoTransferencia}</dd>
            </>
          )}
          {c.atendente && (
            <>
              <dt>Atendente</dt>
              <dd>
                <span className="dono-txt">
                  <span className="dono-ponto" style={{ background: cor.texto }} />
                  {c.atendente.nomeExibicao}
                </span>
              </dd>
            </>
          )}
          <dt>Origem</dt>
          <dd>{ORIGEM[c.origem]}</dd>
          <dt>IA</dt>
          <dd>{situacaoIA(c, config)}</dd>
        </dl>
      </section>

      <section className="sec">
        <h2>Orçamento coletado pela IA</h2>
        <Orcamento conversa={c} eu={eu} aoIrParaMensagem={fechar} />
      </section>

      <section className="sec">
        <h2>
          Pedido
          {c.pedido?.tinyNumero && <span className="h2-extra">Tiny #{c.pedido.tinyNumero}</span>}
        </h2>
        <Pedido conversa={c} eu={eu} />
      </section>

      <section className="sec">
        <h2>Mídias</h2>
        <Midias conversa={c} />
      </section>

      <section className="sec">
        <h2>Cadastro no Tiny</h2>
        <CadastroTiny contatoId={c.contato.id} conversaId={c.id} aoColar={fechar} />
      </section>

      <section className="sec">
        <h2>
          Etiquetas
          <button className="h2-acao" onClick={etiquetas}>
            Editar
          </button>
        </h2>
        {c.etiquetas.length ? (
          <div className="etiquetas">
            {c.etiquetas.map((t) => (
              <span key={t.id} style={{ '--etq': t.cor } as React.CSSProperties}>
                <span className="etq-ponto" />
                {t.nome}
              </span>
            ))}
          </div>
        ) : (
          <p className="nota-sec">Sem etiquetas.</p>
        )}
      </section>
    </aside>
  );
}

function Orcamento({
  conversa,
  eu,
  aoIrParaMensagem,
}: {
  conversa: ConversaDetalheDTO;
  eu: AtendenteDTO;
  aoIrParaMensagem: () => void;
}) {
  const toast = useToast();
  const orcamento = conversa.orcamento;
  if (!orcamento || orcamento.itens.length === 0) {
    return <p className="nota-sec">A IA ainda não coletou dados de orçamento nesta conversa.</p>;
  }
  const varios = orcamento.itens.length > 1;
  const alternativas = orcamento.itens.filter((i) => i.alternativa).length;

  function irPara(mensagemId: string) {
    const alvo = document.getElementById(`msg-${mensagemId}`);
    if (!alvo) {
      toast('Essa mensagem não está mais carregada no chat', 'erro');
      return;
    }
    aoIrParaMensagem();
    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    alvo.classList.remove('destacada');
    void alvo.offsetWidth;
    alvo.classList.add('destacada');
    window.setTimeout(() => alvo.classList.remove('destacada'), 1800);
  }

  const valor = (
    origens: OrigensOrcamento,
    campo: keyof OrigensOrcamento,
    v: string | number | null,
    sufixo = '',
  ) => {
    if (v === null || v === '') return <dd className="falta">falta</dd>;
    const texto = typeof v === 'number' ? `${v.toLocaleString('pt-BR')}${sufixo}` : v;
    const origem = origens[campo];
    if (!origem) return <dd>{texto}</dd>;
    return (
      <dd>
        <button
          className="orc-origem"
          title="Ver a mensagem de onde a IA tirou isso"
          onClick={() => irPara(origem)}
        >
          {texto}
        </button>
      </dd>
    );
  };

  return (
    <div className="orcamento">
      {alternativas > 1 && (
        <p className="nota-sec">
          O cliente está comparando {alternativas} opções e vai escolher uma.
        </p>
      )}
      {orcamento.itens.map((i, n) => (
        <div key={i.id} className="orc-item">
          {varios && (
            <p className="orc-titulo">
              Item {n + 1} ·{' '}
              {i.origens.material ? (
                <button className="orc-origem" onClick={() => irPara(i.origens.material!)}>
                  {i.material ?? 'material a definir'}
                </button>
              ) : (
                (i.material ?? 'material a definir')
              )}
              {(i.apelido || i.alternativa) && (
                <span className="orc-extra">
                  {i.apelido && <span className="orc-apelido">{i.apelido}</span>}
                  {i.alternativa && <span className="orc-alternativa">alternativa</span>}
                </span>
              )}
            </p>
          )}
          {!varios && (i.apelido || i.alternativa) && (
            <p className="orc-titulo">
              <span className="orc-extra">
                {i.apelido && <span className="orc-apelido">{i.apelido}</span>}
                {i.alternativa && <span className="orc-alternativa">alternativa</span>}
              </span>
            </p>
          )}
          <dl className="kv">
            {!varios && (
              <>
                <dt>Material</dt>
                {valor(i.origens, 'material', i.material)}
              </>
            )}
            <dt>Quantidade</dt>
            {valor(i.origens, 'quantidade', i.quantidade, ' un')}
            <dt>Tamanho</dt>
            {valor(i.origens, 'tamanho', i.tamanho)}
            <dt>Cor</dt>
            {valor(i.origens, 'cores', i.cores)}
            <dt>Arte</dt>
            {valor(i.origens, 'arte', i.arte)}
          </dl>
          <CalcularItem item={i} conversa={conversa} eu={eu} aoColar={aoIrParaMensagem} />
        </div>
      ))}
    </div>
  );
}
